import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { KPICard } from './KPICard';
import './RetentionMetrics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface RetentionMetricsProps {
  data: {
    retention_rates: {
      [key: string]: {
        retention_rate: number;
        cohort_size: number;
        retained_users: number;
      };
    };
  };
  loading?: boolean;
}

export const RetentionMetrics: React.FC<RetentionMetricsProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="retention-metrics">
        <h3>Retention Rates</h3>
        <div className="retention-cards">
          <KPICard title="1-Day" value="0%" loading={true} />
          <KPICard title="7-Day" value="0%" loading={true} />
          <KPICard title="14-Day" value="0%" loading={true} />
          <KPICard title="30-Day" value="0%" loading={true} />
        </div>
      </div>
    );
  }

  if (!data || !data.retention_rates) {
    return (
      <div className="retention-metrics">
        <h3>Retention Rates</h3>
        <p className="no-data">No retention data available</p>
      </div>
    );
  }

  const rates = data.retention_rates;

  // Prepare data for bar chart
  const chartLabels = Object.keys(rates).map(key => {
    const dayNum = key.replace('day_', '');
    return `${dayNum} Day${dayNum === '1' ? '' : 's'}`;
  });

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Retention Rate',
        data: Object.values(rates).map(r => r.retention_rate),
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(255, 99, 132, 0.8)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context: any) {
            const rate = context.parsed.y;
            const dayKey = `day_${context.label.split(' ')[0]}`;
            const info = rates[dayKey];
            return [
              `Retention: ${rate.toFixed(1)}%`,
              `Retained: ${info.retained_users} / ${info.cohort_size} users`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  return (
    <div className="retention-metrics">
      <h3>Retention Rates</h3>

      <div className="retention-cards">
        {rates.day_1 && (
          <KPICard
            title="1-Day Retention"
            value={`${rates.day_1.retention_rate.toFixed(1)}%`}
            subtitle={`${rates.day_1.retained_users} of ${rates.day_1.cohort_size} users`}
            icon="📈"
          />
        )}
        {rates.day_7 && (
          <KPICard
            title="7-Day Retention"
            value={`${rates.day_7.retention_rate.toFixed(1)}%`}
            subtitle={`${rates.day_7.retained_users} of ${rates.day_7.cohort_size} users`}
            icon="📊"
          />
        )}
        {rates.day_14 && (
          <KPICard
            title="14-Day Retention"
            value={`${rates.day_14.retention_rate.toFixed(1)}%`}
            subtitle={`${rates.day_14.retained_users} of ${rates.day_14.cohort_size} users`}
            icon="📉"
          />
        )}
        {rates.day_30 && (
          <KPICard
            title="30-Day Retention"
            value={`${rates.day_30.retention_rate.toFixed(1)}%`}
            subtitle={`${rates.day_30.retained_users} of ${rates.day_30.cohort_size} users`}
            icon="🎯"
          />
        )}
      </div>

      <div className="retention-chart">
        <h4>Retention Drop-off</h4>
        <div className="chart-wrapper" style={{ height: '250px' }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default RetentionMetrics;
